import '@theme/product-form';

class PackageProductSelector extends HTMLElement {
  #products = [];
  #selectedVariant = null;
  #primaryAvailable = false;
  #primaryVariantId = '';
  #privateMode = false;
  #requiredTicketQuantity = 0;
  #maximumTicketQuantity = 0;
  #submitting = false;
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#primaryAvailable = this.dataset.primaryAvailable === 'true';
    this.#primaryVariantId = this.dataset.primaryVariantId || '';
    this.#privateMode = this.dataset.privateMode === 'true';
    this.#requiredTicketQuantity = Number(this.dataset.requiredTicketQuantity || 0);
    this.#maximumTicketQuantity = Number(
      this.dataset.maximumTicketQuantity || this.#requiredTicketQuantity
    );

    try {
      this.#products = JSON.parse(this.querySelector('[data-package-products]')?.textContent || '[]');
    } catch {
      this.#products = [];
    }

    const { signal } = this.#abortController;
    this.addEventListener('invalid', this.#handleInvalid, { capture: true, signal });
    this.quantityInput?.addEventListener('change', this.#handleQuantityChange, { signal });
    this.quantityDecreaseButton?.addEventListener('click', () => this.#stepQuantity(-1), {
      signal,
    });
    this.quantityIncreaseButton?.addEventListener('click', () => this.#stepQuantity(1), {
      signal,
    });
    this.locationSelect?.addEventListener('change', this.#handleLocationChange, { signal });
    this.sessionSelect?.addEventListener('change', this.#handleSessionChange, { signal });
    this.attendeesInput?.addEventListener('change', this.#handleAttendeesChange, { signal });
    this.attendeesInput?.addEventListener('input', this.#handleAttendeesInput, { signal });
    this.attendeesDecreaseButton?.addEventListener('click', () => this.#stepAttendees(-1), {
      signal,
    });
    this.attendeesIncreaseButton?.addEventListener('click', () => this.#stepAttendees(1), {
      signal,
    });

    const eventTarget = this.closest('.shopify-section, dialog') || document;
    eventTarget.addEventListener('variant:selected', this.#handleVariantSelected, { signal });
    eventTarget.addEventListener('variant:update', this.#handleVariantUpdate, { signal });

    this.#syncModeUi({ resetQuantity: true });
    this.#configureLocations();
    this.#updateState();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get locationSelect() {
    return this.querySelector('[data-package-location]');
  }

  get sessionSelect() {
    return this.querySelector('[data-package-session]');
  }

  get quantityInput() {
    return this.querySelector('[data-package-quantity]');
  }

  get ticketField() {
    return this.querySelector('[data-package-ticket-field]');
  }

  get quantityDecreaseButton() {
    return this.querySelector('[data-package-quantity-decrease]');
  }

  get quantityIncreaseButton() {
    return this.querySelector('[data-package-quantity-increase]');
  }

  get attendeesField() {
    return this.querySelector('[data-package-attendees-field]');
  }

  get privateNotice() {
    return this.querySelector('[data-package-private-notice]');
  }

  get attendeesInput() {
    return this.querySelector('[data-package-attendees]');
  }

  get attendeesDecreaseButton() {
    return this.querySelector('[data-package-attendees-decrease]');
  }

  get attendeesIncreaseButton() {
    return this.querySelector('[data-package-attendees-increase]');
  }

  get messageElement() {
    return this.querySelector('[data-package-message]');
  }

  get addToCartContainer() {
    return this.closest('product-form-component')?.querySelector('add-to-cart-component');
  }

  getCartItem() {
    if (!this.#isReady()) {
      this.reportValidity();
      return null;
    }

    return {
      variantId: this.#selectedVariant.id,
      quantity: Number(this.quantityInput.value),
      privateMode: this.#privateMode,
      sessionCapacity: this.#privateMode ? Number(this.#selectedVariant.sessionCapacity) : null,
      attendeeCount: this.#privateMode ? Number(this.attendeesInput?.value) : null,
    };
  }

  async prepareCartItem() {
    if (!this.getCartItem()) return null;

    this.setSubmitting(true);
    const selectedLocationId = this.locationSelect?.value;
    const selectedVariantId = this.sessionSelect?.value;
    const selectedTicketQuantity = this.quantityInput?.value;
    const primaryVariantId =
      this.closest('product-form-component')?.querySelector('[ref="variantId"]')?.value;

    try {
      const productUrl = this.closest('product-form-component')?.dataset.productUrl;
      if (!productUrl) throw new Error('Package product URL missing');

      const sectionId = this.closest('product-form-component')?.dataset.sectionId;
      if (!sectionId) throw new Error('Package section ID missing');

      const url = new URL(productUrl, window.location.origin);
      const currentUrl = new URL(window.location.href);
      for (const parameter of ['preview_theme_id', '_fd', 'pb']) {
        const value = currentUrl.searchParams.get(parameter);
        if (value !== null) url.searchParams.set(parameter, value);
      }
      if (primaryVariantId) url.searchParams.set('variant', primaryVariantId);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_package_refresh', Date.now().toString());
      const response = await fetch(url, {
        cache: 'no-store',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Package availability request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productId = this.closest('product-form-component')?.dataset.productId;
      const refreshedSelector = Array.from(html.querySelectorAll('package-product-selector')).find(
        (selector) =>
          (!primaryVariantId ||
            String(selector.dataset.primaryVariantId) === String(primaryVariantId)) &&
          (!productId ||
            selector.closest('product-form-component')?.dataset.productId === productId)
      );
      if (!refreshedSelector) throw new Error('Package availability selector missing');

      const refreshedProducts = JSON.parse(
        refreshedSelector.querySelector('[data-package-products]')?.textContent || '[]'
      );

      if (!refreshedProducts.length) throw new Error('Package availability data missing');

      this.#products = refreshedProducts;
      this.#applyPrimaryConfiguration(refreshedSelector);
      if (!this.#privateMode && selectedTicketQuantity) {
        this.quantityInput.value = selectedTicketQuantity;
        this.#normalizeTicketQuantity();
      }
      this.#configureLocations();
      const location = this.#products.find(
        (product) => String(product.id) === String(selectedLocationId)
      );
      this.#populateSessions(location, selectedVariantId, false);

      if (!this.#isReady()) {
        this.#setMessage(
          this.#privateMode ? this.dataset.privateSessionChanged : this.dataset.sessionChanged,
          true
        );
        return null;
      }

      return this.getCartItem();
    } catch (error) {
      console.error('Unable to refresh package availability:', error);
      this.#setMessage(
        this.dataset.refreshError ||
          "We couldn't verify this session's availability. Refresh the page and try again.",
        true
      );
      return null;
    } finally {
      this.setSubmitting(false);
    }
  }

  reportValidity() {
    if (!this.#privateMode && !this.#hasTicketQuantityRange()) {
      this.#setMessage(this.dataset.ticketQuantityConfigurationError, true);
      return;
    }

    if (!this.#privateMode && !this.#hasValidTicketQuantity()) {
      this.#setMessage(
        this.dataset.ticketQuantityValidationMessage || 'Choose a valid total ticket quantity.',
        true
      );
      this.quantityInput?.reportValidity();
      return;
    }

    this.#setMessage(
      this.#privateMode ? this.dataset.privateValidationMessage : this.dataset.validationMessage,
      true
    );

    if (!this.locationSelect?.value) {
      this.locationSelect?.reportValidity();
      return;
    }

    if (!this.sessionSelect?.value) {
      this.sessionSelect?.reportValidity();
      return;
    }

    if (this.#privateMode) {
      this.attendeesInput?.reportValidity();
    }
  }

  setSubmitting(isSubmitting) {
    this.#submitting = isSubmitting;
    this.#updateAddButton();
  }

  showError(message) {
    this.#setMessage(message, true);
  }

  #handleInvalid = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;

    if (field.closest('[hidden]')) {
      event.preventDefault();
      this.#setMessage(
        "We couldn't confirm the ticket quantity. Choose the location and session again.",
        true
      );
      return;
    }

    const label = field.labels?.[0]?.textContent?.trim();
    this.#setMessage(`${label ? `${label}: ` : ''}${field.validationMessage}`, true);
  };

  #handleQuantityChange = () => {
    this.#applyTicketQuantityChange();
  };

  #stepQuantity(direction) {
    if (!this.quantityInput || this.quantityInput.disabled) return;

    this.quantityInput.value = String(Number(this.quantityInput.value) + direction);
    this.#applyTicketQuantityChange();
  }

  #applyTicketQuantityChange() {
    const selectedLocationId = this.locationSelect?.value;
    const selectedVariantId = this.sessionSelect?.value;

    this.#normalizeTicketQuantity();
    this.#configureLocations();

    const location = this.#products.find(
      (product) => String(product.id) === String(this.locationSelect?.value)
    );
    const canPreserveSession =
      String(this.locationSelect?.value) === String(selectedLocationId);
    this.#populateSessions(location, canPreserveSession ? selectedVariantId : '', false);

    if (selectedVariantId && String(this.sessionSelect?.value) !== String(selectedVariantId)) {
      this.#setMessage(this.dataset.sessionChanged, true);
    } else {
      this.#setMessage('');
    }

    this.#updateState();
  }

  #handleLocationChange = () => {
    const location = this.#products.find(
      (product) => String(product.id) === String(this.locationSelect?.value)
    );

    this.#selectedVariant = null;
    this.#populateSessions(location);
    this.#setMessage('');
    this.#updateState();
  };

  #handleSessionChange = () => {
    const location = this.#products.find(
      (product) => String(product.id) === String(this.locationSelect?.value)
    );

    this.#selectedVariant =
      location?.variants?.find(
        (variant) => String(variant.id) === String(this.sessionSelect?.value)
      ) || null;

    this.#configureQuantity();
    this.#setMessage('');
    this.#updateState();
  };

  #handleAttendeesChange = () => {
    this.#normalizeAttendees();
    this.#setMessage('');
    this.#updateState();
  };

  #handleAttendeesInput = () => {
    this.#setMessage('');
    this.#updateState();
  };

  #handleVariantSelected = (event) => {
    const formProductId = this.closest('product-form-component')?.dataset.productId;
    const variantPicker =
      event.target instanceof HTMLElement ? event.target.closest('variant-picker') : null;
    const productId = variantPicker?.dataset.productId;
    if (!productId || !formProductId || String(productId) !== String(formProductId)) return;

    this.#primaryAvailable = false;
    this.#updateState();
  };

  #handleVariantUpdate = (event) => {
    const formProductId = this.closest('product-form-component')?.dataset.productId;
    const productId = event.detail?.data?.productId;
    if (!productId || !formProductId || String(productId) !== String(formProductId)) return;

    const primaryVariantId = event.detail?.resource?.id;
    const refreshedSelector = Array.from(
      event.detail?.data?.html?.querySelectorAll('package-product-selector') || []
    ).find(
      (selector) =>
        String(selector.dataset.primaryVariantId) === String(primaryVariantId) &&
        selector.closest('product-form-component')?.dataset.productId === formProductId
    );

    this.#primaryAvailable = Boolean(event.detail?.resource?.available);
    if (!refreshedSelector) {
      this.#setMessage(this.dataset.configurationError, true);
      this.#updateState();
      return;
    }

    try {
      const refreshedProducts = JSON.parse(
        refreshedSelector.querySelector('[data-package-products]')?.textContent || '[]'
      );
      if (refreshedProducts.length) this.#products = refreshedProducts;
    } catch {
      this.#setMessage(this.dataset.configurationError, true);
      this.#updateState();
      return;
    }

    const primaryVariantChanged =
      String(this.#primaryVariantId) !== String(refreshedSelector.dataset.primaryVariantId);
    const selectedLocationId = this.locationSelect?.value;
    const selectedSessionId = this.sessionSelect?.value;

    this.#applyPrimaryConfiguration(refreshedSelector, {
      resetQuantity: primaryVariantChanged,
    });
    this.#configureLocations();

    const location = this.#products.find(
      (product) =>
        String(product.id) ===
        String(
          String(this.locationSelect?.value) === String(selectedLocationId)
            ? selectedLocationId
            : ''
        )
    );
    this.#populateSessions(location, primaryVariantChanged ? '' : selectedSessionId, false);
    this.#setMessage('');
    queueMicrotask(() => this.#updateState());
  };

  #applyPrimaryConfiguration(selector, { resetQuantity = false } = {}) {
    this.#primaryAvailable = selector.dataset.primaryAvailable === 'true';
    this.#primaryVariantId = selector.dataset.primaryVariantId || '';
    this.#privateMode = selector.dataset.privateMode === 'true';
    this.#requiredTicketQuantity = Number(selector.dataset.requiredTicketQuantity || 0);
    this.#maximumTicketQuantity = Number(
      selector.dataset.maximumTicketQuantity || this.#requiredTicketQuantity
    );

    this.dataset.primaryAvailable = String(this.#primaryAvailable);
    this.dataset.primaryVariantId = this.#primaryVariantId;
    this.dataset.privateMode = String(this.#privateMode);
    this.dataset.requiredTicketQuantity = String(this.#requiredTicketQuantity);
    this.dataset.maximumTicketQuantity = String(this.#maximumTicketQuantity);
    this.#syncModeUi({ resetQuantity });
  }

  #syncModeUi({ resetQuantity = false } = {}) {
    if (this.ticketField instanceof HTMLElement) {
      this.ticketField.hidden =
        this.#privateMode ||
        !this.#hasTicketQuantityRange() ||
        this.#maximumTicketQuantity === this.#requiredTicketQuantity;
    }
    if (this.attendeesField instanceof HTMLElement) {
      this.attendeesField.hidden = !this.#privateMode;
    }
    if (this.privateNotice instanceof HTMLElement) {
      this.privateNotice.hidden = !this.#privateMode;
    }

    if (this.attendeesInput) {
      this.attendeesInput.required = this.#privateMode;
      if (!this.#privateMode) this.attendeesInput.disabled = true;
    }

    if (this.quantityInput) {
      if (this.#privateMode) {
        this.quantityInput.value = '';
        this.quantityInput.disabled = true;
      } else {
        this.quantityInput.min = String(this.#requiredTicketQuantity);
        this.quantityInput.max = String(this.#maximumAllowedTicketQuantity());
        this.quantityInput.step = '1';
        this.quantityInput.disabled = !this.#hasTicketQuantityRange();

        if (resetQuantity || !this.#hasValidTicketQuantity()) {
          this.quantityInput.value = this.#hasTicketQuantityRange()
            ? String(this.#requiredTicketQuantity)
            : '';
        }
        this.#normalizeTicketQuantity();
      }
    }

    this.#updateTicketButtons();
  }

  #configureLocations() {
    if (!this.locationSelect) return;

    const selectedLocationId = this.locationSelect.value;
    this.locationSelect.replaceChildren(
      new Option(this.dataset.locationPlaceholder || 'Choose a location', '')
    );

    for (const location of this.#products) {
      const hasSelectableSession = (location.variants || []).some((variant) =>
        this.#sessionIsSelectable(variant)
      );
      let suffix = '';
      if (!hasSelectableSession) {
        suffix = this.#privateMode
          ? ` — ${this.dataset.privateLocationUnavailable || 'No private sessions available'}`
          : ` — ${
              this.dataset.standardLocationUnavailable || 'No sessions have enough tickets'
            }`;
      }
      const option = new Option(`${location.title}${suffix}`, String(location.id));
      option.disabled = !hasSelectableSession;
      this.locationSelect.append(option);
    }

    if (
      selectedLocationId &&
      Array.from(this.locationSelect.options).some(
        (option) => option.value === selectedLocationId && !option.disabled
      )
    ) {
      this.locationSelect.value = selectedLocationId;
    }
  }

  #populateSessions(location, selectedVariantId = '', autoSelectSingle = true) {
    if (!this.sessionSelect) return;

    this.sessionSelect.replaceChildren();
    this.sessionSelect.append(new Option(this.dataset.sessionPlaceholder || 'Choose a session', ''));

    for (const variant of location?.variants || []) {
      const isSelectable = this.#sessionIsSelectable(variant);
      const unavailableLabel = this.#sessionUnavailableLabel(variant);
      const option = new Option(
        `${variant.title}${isSelectable ? '' : ` — ${unavailableLabel}`}`,
        String(variant.id)
      );
      option.disabled = !isSelectable;
      this.sessionSelect.append(option);
    }

    this.sessionSelect.disabled = !location;

    const selectableVariants = (location?.variants || []).filter((variant) =>
      this.#sessionIsSelectable(variant)
    );

    const selectedVariant =
      selectableVariants.find((variant) => String(variant.id) === String(selectedVariantId)) ||
      (autoSelectSingle && selectableVariants.length === 1 ? selectableVariants[0] : null);

    if (selectedVariant) {
      this.sessionSelect.value = String(selectedVariant.id);
      this.#selectedVariant = selectedVariant;
      this.#configureQuantity();
    } else {
      this.#selectedVariant = null;
      this.#configureQuantity();
    }
  }

  #configureQuantity() {
    if (!this.quantityInput) return;

    if (this.#privateMode) {
      if (!this.#selectedVariant) {
        this.quantityInput.value = '';
        this.quantityInput.disabled = true;
        this.#configureAttendees();
        return;
      }

      const capacity = Number(this.#selectedVariant.sessionCapacity);
      this.quantityInput.min = String(capacity);
      this.quantityInput.max = String(capacity);
      this.quantityInput.value = String(capacity);
      this.quantityInput.disabled = false;
      this.#configureAttendees();
      return;
    }

    this.quantityInput.disabled = !this.#hasTicketQuantityRange();
    this.#normalizeTicketQuantity();
    this.#updateTicketButtons();
    this.#configureAttendees();
  }

  #configureAttendees() {
    if (!this.#privateMode || !this.attendeesInput) return;

    if (!this.#selectedVariant) {
      this.attendeesInput.disabled = true;
      if (this.attendeesDecreaseButton) this.attendeesDecreaseButton.disabled = true;
      if (this.attendeesIncreaseButton) this.attendeesIncreaseButton.disabled = true;
      return;
    }

    const capacity = Number(this.#selectedVariant.sessionCapacity);
    this.attendeesInput.min = '1';
    this.attendeesInput.max = String(capacity);
    this.attendeesInput.step = '1';
    this.attendeesInput.disabled = false;
    this.#normalizeAttendees();
  }

  #stepAttendees(direction) {
    if (!this.attendeesInput || this.attendeesInput.disabled) return;

    this.attendeesInput.value = String(Number(this.attendeesInput.value) + direction);
    this.#normalizeAttendees();
    this.#setMessage('');
    this.#updateState();
  }

  #normalizeAttendees() {
    if (!this.attendeesInput || !this.#selectedVariant) return;

    const capacity = Number(this.#selectedVariant.sessionCapacity);
    const enteredValue = Number(this.attendeesInput.value);
    const attendeeCount = Number.isInteger(enteredValue) ? enteredValue : 1;

    this.attendeesInput.value = String(Math.min(Math.max(attendeeCount, 1), capacity));
    this.#updateAttendeeButtons();
  }

  #updateAttendeeButtons() {
    if (!this.attendeesInput || !this.#selectedVariant) return;

    const attendeeCount = Number(this.attendeesInput.value);
    const capacity = Number(this.#selectedVariant.sessionCapacity);
    if (this.attendeesDecreaseButton) {
      this.attendeesDecreaseButton.disabled = this.attendeesInput.disabled || attendeeCount <= 1;
    }
    if (this.attendeesIncreaseButton) {
      this.attendeesIncreaseButton.disabled =
        this.attendeesInput.disabled || attendeeCount >= capacity;
    }
  }

  #normalizeTicketQuantity() {
    if (!this.quantityInput || this.#privateMode || !this.#hasTicketQuantityRange()) return;

    const enteredValue = Number(this.quantityInput.value);
    const ticketQuantity = Number.isInteger(enteredValue)
      ? enteredValue
      : this.#requiredTicketQuantity;

    this.quantityInput.value = String(
      Math.min(
        Math.max(ticketQuantity, this.#requiredTicketQuantity),
        this.#maximumAllowedTicketQuantity()
      )
    );
    this.#updateTicketButtons();
  }

  #updateTicketButtons() {
    if (!this.quantityInput || this.#privateMode) {
      if (this.quantityDecreaseButton) this.quantityDecreaseButton.disabled = true;
      if (this.quantityIncreaseButton) this.quantityIncreaseButton.disabled = true;
      return;
    }

    const ticketQuantity = Number(this.quantityInput.value);
    if (this.quantityDecreaseButton) {
      this.quantityDecreaseButton.disabled =
        this.quantityInput.disabled || ticketQuantity <= this.#requiredTicketQuantity;
    }
    if (this.quantityIncreaseButton) {
      this.quantityIncreaseButton.disabled =
        this.quantityInput.disabled || ticketQuantity >= this.#maximumAllowedTicketQuantity();
    }
  }

  #minimum(variant) {
    return Math.max(1, Number(variant?.quantityRule?.min || 1));
  }

  #increment(variant) {
    return Math.max(1, Number(variant?.quantityRule?.increment || 1));
  }

  #availableQuantity(variant) {
    const ruleMaximum =
      variant?.quantityRule?.max == null ? Number.POSITIVE_INFINITY : Number(variant.quantityRule.max);
    const isInventoryLimited =
      Boolean(variant?.inventoryManagement) && variant?.inventoryPolicy !== 'continue';
    const inventoryMaximum = isInventoryLimited
      ? Math.max(0, Number(variant.inventoryQuantity || 0))
      : Number.POSITIVE_INFINITY;

    return Math.min(ruleMaximum, inventoryMaximum);
  }

  #hasRequiredTicketQuantity() {
    return Number.isInteger(this.#requiredTicketQuantity) && this.#requiredTicketQuantity > 0;
  }

  #maximumAllowedTicketQuantity() {
    return Number.isInteger(this.#maximumTicketQuantity) &&
      this.#maximumTicketQuantity >= this.#requiredTicketQuantity
      ? this.#maximumTicketQuantity
      : this.#requiredTicketQuantity;
  }

  #hasTicketQuantityRange() {
    return Boolean(
      this.#hasRequiredTicketQuantity() &&
        Number.isInteger(this.#maximumTicketQuantity) &&
        this.#maximumTicketQuantity >= this.#requiredTicketQuantity
    );
  }

  #hasValidTicketQuantity() {
    const quantity = Number(this.quantityInput?.value);

    return Boolean(
      this.#hasTicketQuantityRange() &&
        Number.isInteger(quantity) &&
        quantity >= this.#requiredTicketQuantity &&
        quantity <= this.#maximumAllowedTicketQuantity()
    );
  }

  #sessionIsSelectable(variant) {
    if (!this.#privateMode) {
      if (!this.#hasValidTicketQuantity()) return false;

      const requiredQuantity = Number(this.quantityInput?.value);
      const minimum = this.#minimum(variant);
      const increment = this.#increment(variant);

      return Boolean(
        variant?.available &&
          variant?.inventoryManagement &&
          variant?.inventoryPolicy !== 'continue' &&
          this.#availableQuantity(variant) >= requiredQuantity &&
          requiredQuantity >= minimum &&
          (requiredQuantity - minimum) % increment === 0
      );
    }

    const capacity = Number(variant?.sessionCapacity);
    const minimum = this.#minimum(variant);
    const increment = this.#increment(variant);
    const ruleMaximum =
      variant?.quantityRule?.max == null ? Number.POSITIVE_INFINITY : Number(variant.quantityRule.max);

    return Boolean(
      variant?.available &&
        Number.isInteger(capacity) &&
        capacity > 0 &&
        variant?.inventoryManagement &&
        variant?.inventoryPolicy !== 'continue' &&
        Number(variant.inventoryQuantity) === capacity &&
        capacity >= minimum &&
        capacity <= ruleMaximum &&
        (capacity - minimum) % increment === 0
    );
  }

  #sessionUnavailableLabel(variant) {
    if (this.#privateMode) {
      return this.dataset.privateSessionUnavailable || 'Not available for private booking';
    }

    if (!this.#hasTicketQuantityRange()) {
      return this.dataset.ticketQuantityConfigurationError || 'Package ticket quantity unavailable';
    }

    if (!variant?.available) {
      return this.dataset.soldOutLabel || 'Sold out';
    }

    if (!variant?.inventoryManagement || variant?.inventoryPolicy === 'continue') {
      return this.dataset.unverifiedInventoryLabel || "Availability can't be verified";
    }

    const availableQuantity = Math.max(0, Number(variant.inventoryQuantity || 0));
    const requiredQuantity = Number(this.quantityInput?.value);
    if (availableQuantity < requiredQuantity) {
      return `${availableQuantity} tickets remaining — ${requiredQuantity} needed`;
    }

    return this.dataset.soldOutLabel || 'Unavailable';
  }

  #isReady() {
    const quantity = Number(this.quantityInput?.value);
    const attendeeCount = Number(this.attendeesInput?.value);
    const capacity = Number(this.#selectedVariant?.sessionCapacity);
    const hasValidAttendeeCount =
      !this.#privateMode ||
      (Number.isInteger(attendeeCount) && attendeeCount >= 1 && attendeeCount <= capacity);

    return Boolean(
      this.#products.length &&
        this.#primaryAvailable &&
        this.locationSelect?.value &&
        this.#sessionIsSelectable(this.#selectedVariant) &&
        Number.isInteger(quantity) &&
        hasValidAttendeeCount &&
        (this.#privateMode
          ? quantity === capacity
          : this.#hasValidTicketQuantity())
    );
  }

  #updateState() {
    const hasConfiguration = this.#products.length > 0;
    const hasTicketQuantity = this.#privateMode || this.#hasTicketQuantityRange();

    if (!hasConfiguration) {
      this.#setMessage(this.dataset.configurationError, true);
    } else if (!hasTicketQuantity) {
      this.#setMessage(this.dataset.ticketQuantityConfigurationError, true);
    }

    if (!this.#selectedVariant) {
      if (this.quantityInput && this.#privateMode) this.quantityInput.disabled = true;
      if (this.attendeesInput) this.attendeesInput.disabled = true;
      if (this.attendeesDecreaseButton) this.attendeesDecreaseButton.disabled = true;
      if (this.attendeesIncreaseButton) this.attendeesIncreaseButton.disabled = true;
    } else if (this.#privateMode) {
      this.#updateAttendeeButtons();
    }

    this.#updateTicketButtons();
    this.#updateAddButton();
  }

  #updateAddButton() {
    const addToCartContainer = this.addToCartContainer;
    if (!addToCartContainer) return;

    const button = addToCartContainer.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (this.#submitting) {
      button.dataset.packageLoading = 'true';
      button.setAttribute('aria-busy', 'true');
    } else {
      delete button.dataset.packageLoading;
      button.removeAttribute('aria-busy');
    }

    button.disabled = this.#submitting || !this.#isReady();

    if (!button.disabled) {
      if (button && this.dataset.addButtonLabel) {
        const text =
          button.querySelector('.add-to-cart-text__content span span') ||
          button.querySelector('.add-to-cart-text__content');
        if (text) text.textContent = this.dataset.addButtonLabel;
      }
    }
  }

  #setMessage(message = '', isError = false) {
    if (!this.messageElement) return;

    this.messageElement.textContent = message || '';
    this.messageElement.dataset.error = isError ? 'true' : 'false';
  }
}

if (!customElements.get('package-product-selector')) {
  customElements.define('package-product-selector', PackageProductSelector);
}
